import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MunicipalityManagementConfigurationStore } from '@app/municipality-management/services/municipality-management-configuration.store';
import { AddEditRegionDialogComponent } from '@app/region-management/dialog/add-edit-region-dialog/add-edit-region-dialog.component';
import { RegionManagementDataService } from '@app/region-management/services/region-management-data.service';
import { Item, ListTreeComponent, Node } from '@app/shared/components/list-tree/list-tree.component';
import { Strings } from '@app/shared/constants';
import { ListTreeDialogComponent } from '@app/shared/dialog/list-tree-dialog/list-tree-dialog.component';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { GemisTenant, RegionTypeEnum } from '@app/shared/enums';
import { MunicipalityService } from '@app/shared/services/api/municipality.service';
import { MunicipalityRegionHierarchy, RegionMunicipalitiesService } from '@app/shared/services/api/region-municipalities.service';
import { Region, RegionService, RegionType } from '@app/shared/services/api/regions.service';
import { AuthService } from '@app/shared/services/auth.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { listToTree } from '@app/shared/utility';
import { cssVariables } from '@env/css-vars';
import { cloneDeep, differenceWith, get, isEmpty, isNil, maxBy, orderBy, toLower, trim, uniqBy } from 'lodash';
import { Observable, Subject, forkJoin, of, throwError } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ReassignKamDialogComponent } from './dialog/reassign-kam-dialog/reassign-kam-dialog.component';
import { ReassignmentDialogComponent } from './dialog/reassignment-dialog/reassignment-dialog.component';
import { NullableBoolean, NullableString } from '@app/shared/models/nullable';
import { HttpErrorResponse } from '@angular/common/http';
import { GeneralErrorDialogComponent } from '@app/shared/dialog/general-error-dialog/general-error-dialog.component';
import { IGeneralErrorDialogComponent } from '@app/shared/models/dialog/general-error-dialog';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';

type TreeNode = {
  guid: string;
  name: string;
  parentId: string;
  selectable: boolean;
  data: {
    guid: string;
    name: string;
    type: {
      guid: NullableString;
      name: NullableString;
      code: NullableString;
    };
    isAdd: boolean;
    isEdit: boolean;
    isDelete: boolean;
  };
};

@Component({
  selector: 'app-region-management',
  templateUrl: './region-management.component.html',
  styleUrls: ['./region-management.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RegionManagementComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  backOfficeRegions: any[] = [];
  serviceRegions: any[] = [];
  adminRegions: any[] = [];
  poHoldingRegions: any[] = [];
  public orphanMunicipalities?: any[];
  allRegionTypes: RegionType[] = [];
  parentRegionTypes: any[] = [];
  selectedParentRegion = { name: '' } as RegionType;
  regionSelections: Node[] = [];
  @ViewChild('tree', ) tree: ListTreeComponent | undefined;
  marginAdjustment = 0;
  isElementVisible?: boolean;

  constructor(
    public regionService: RegionService,
    public matDialog: MatDialog,
    private translateService: TranslateService,
    private overLoaderService: OverlayLoaderService,
    private regionManagementData: RegionManagementDataService,
    private municipalityService: MunicipalityService,
    private regionMunicipalitiesService: RegionMunicipalitiesService,
    private authService: AuthService,
    private _configStore: MunicipalityManagementConfigurationStore
  ) {}

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this._configStore.hasNoOrphanedMunicipality = undefined;
  }

  public ngOnInit(): void {
    this.overLoaderService.isLoading = true;
    this.overLoaderService.ignoreChanges = true;
    this.loadData()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => this.overLoaderService.setLoadingToFalse(),
        error: () => this.overLoaderService.setLoadingToFalse(),
      });
  }

  loadData(selectedHierarchy?: any) {
    this.backOfficeRegions = [];
    this.serviceRegions = [];
    this.adminRegions = [];
    this.orphanMunicipalities = [];
    this.allRegionTypes = [];
    this.parentRegionTypes = [];
    this.regionSelections = [];
    this.poHoldingRegions = [];

    /*
      This causes multiple identical API calls
    */
    this.regionManagementData.loadData();

    return this.regionManagementData.getRegionManagementData().pipe(
      tap((data) => {
        data[0] = orderBy(data[0], [(x) => (x.name ? x.name.toLowerCase() : x.name)], 'asc');
        data[1] = orderBy(data[1], [(x) => (x.municipalityName ? x.municipalityName.toLowerCase() : x.municipalityName)], 'asc');
        data[2] = orderBy(data[2], [(x) => (x.name ? x.name.toLowerCase() : x.name)], 'asc');

        this.allRegionTypes = data[0];
        const backOfficeRegionType = this.allRegionTypes.find((regionType: RegionType) => regionType.code === RegionTypeEnum.backOfficeRegion);
        const serviceRegionType = this.allRegionTypes.find((regionType: RegionType) => regionType.code === RegionTypeEnum.serviceRegion);
        const adminRegionType = this.allRegionTypes.find((regionType: RegionType) => regionType.code === RegionTypeEnum.adminAreaRegion);
        const poHoldingRegionType = this.allRegionTypes.find((regionType: RegionType) => regionType.code === RegionTypeEnum.poHoldingRegion);

        const treeList = this.prepareTreeData(data[2], data[1], data[0]);
        const tree = listToTree(treeList, 'guid', 'parentId', this.mapToTreeNodeRegion);

        if (tree.filter((a) => a.item.data.type.guid === poHoldingRegionType?.guid).length <= 0) {
          const poHoldingRegionNode = this.getPoHoldingRegionNode(data[4], poHoldingRegionType);
          tree.push(poHoldingRegionNode);
        }

        this.backOfficeRegions = tree.filter((a) => a.item.data.type.guid === backOfficeRegionType?.guid);
        this.serviceRegions = tree.filter((a) => a.item.data.type.guid === serviceRegionType?.guid);
        const adminRegionList = tree.filter((a) => a.item.data.type.guid === adminRegionType?.guid);
        const unassignedRegion = adminRegionList.find((a) => trim(toLower(a.item.name)) === trim(toLower(Strings.unassignedRegion)));
        if (unassignedRegion) {
          unassignedRegion.item.name = '';
          unassignedRegion.item.data.isEdit = false;
        }

        this.adminRegions = orderBy(adminRegionList, (a) => a.item.name, ['asc']);
        this.poHoldingRegions = tree.filter((a) => a.item.data.type.guid === poHoldingRegionType?.guid);

        if (backOfficeRegionType) {
          backOfficeRegionType['data'] = this.backOfficeRegions;
        }

        if (serviceRegionType) {
          serviceRegionType['data'] = this.serviceRegions;
        }

        if (adminRegionType) {
          adminRegionType['data'] = this.adminRegions;
        }

        if (poHoldingRegionType) {
          poHoldingRegionType['data'] = this.makePoHoldingRegionNotEditable(this.poHoldingRegions);
        }

        this.parentRegionTypes = [backOfficeRegionType, poHoldingRegionType, serviceRegionType, adminRegionType];

        const municipalities = data[3];
        const mappings = uniqBy(data[1], 'municipalityId');
        this.orphanMunicipalities = orderBy(
          municipalities
            .map((a) => {
              const orphan = { ...a, isOrphan: false };
              const regionMunicipalityObject = mappings.find((b) => a.guid === b.municipalityId);

              orphan.isOrphan = this.checkifRegionOrphan(regionMunicipalityObject);
              return orphan;
            })
            .filter((a) => {
              if (a.isOrphan) {
                return true;
              }
              return isNil(a.primaryAdminGuid);
            }),
          ['municipalityName'],
          ['asc']
        );

        this._configStore.hasNoOrphanedMunicipality = !this.orphanMunicipalities || isEmpty(this.orphanMunicipalities);

        const hierarchy = selectedHierarchy ? this.parentRegionTypes.find((a) => a.code === selectedHierarchy.code) : this.parentRegionTypes[0];

        this.selectDropdownParentRegion(hierarchy);
        this.getAddIconMarginAdjustment(hierarchy);
      })
    );
  }

  prepareTreeData(regionList: Region[], municipalityMapList: any, regionTypes: RegionType[]) {
    const adminRegionType = regionTypes.find((regionType: RegionType) => regionType.code === RegionTypeEnum.adminAreaRegion);
    const backOfficeRegionType = regionTypes.find((regionType: RegionType) => regionType.code === RegionTypeEnum.backOfficeRegion);
    const kamRegionType = regionTypes.find((regionType: RegionType) => regionType.code === RegionTypeEnum.kamRegion);
    const serviceRegionType = regionTypes.find((regionType: RegionType) => regionType.code === RegionTypeEnum.serviceRegion);
    const custServiceRegionType = regionTypes.find((regionType: RegionType) => regionType.code === RegionTypeEnum.customerServiceRegion);
    const poHoldingRegiontype = regionTypes.find((regionType: RegionType) => regionType.code === RegionTypeEnum.poHoldingRegion);

    const treeList: TreeNode[] = regionList
      .filter((region: Region) => regionTypes.some((regionType: RegionType) => regionType.guid === region.regionTypeId))
      .map((region: Region) => {
        const isParentRegion =
          region.regionTypeId === backOfficeRegionType?.guid ||
          region.regionTypeId === serviceRegionType?.guid ||
          region.regionTypeId === poHoldingRegiontype?.guid;
        const isChildrenRegion =
          region.regionTypeId === kamRegionType?.guid || region.regionTypeId === custServiceRegionType?.guid || region.regionTypeId === adminRegionType?.guid;

        const regionType = regionTypes.find((regionType: RegionType) => regionType.guid === region.regionTypeId);
        const id = region.guid;
        const name = region.name;
        return {
          guid: id,
          name: name,
          parentId: region.parentRegionId,
          selectable: region.regionTypeId === kamRegionType?.guid || region.regionTypeId === custServiceRegionType?.guid,
          data: {
            guid: id,
            name: name,
            type: {
              guid: regionType?.guid ?? null,
              name: regionType?.name ?? null,
              code: regionType?.code ?? null,
            },
            isAdd: isParentRegion,
            isEdit: isParentRegion || isChildrenRegion,
            isDelete: (isParentRegion || isChildrenRegion) && region.regionTypeId !== poHoldingRegiontype?.guid,
          },
        };
      });

    municipalityMapList.forEach((mapping: any) => {
      const id = mapping.municipalityId;
      const name = `${mapping.municipalityName} (${mapping.municipalityKey})`;
      const municipality = {
        guid: id,
        name: name,
        selectable: true,
        data: {
          guid: id,
          name: name,
          municipalityKey: mapping.municipalityKey,
          type: {
            guid: null,
            name: null,
            code: 'MUNICIPALITY',
          },
          primaryKam: {
            guid: mapping.primaryAdminGuid,
            firstName: mapping.primaryAdminFirstName,
            lastName: mapping.primaryAdminLastName,
          },
          regionMapping: mapping,
          isAdd: false,
          isEdit: false,
          isDelete: false,
        },
      };

      treeList.push({ ...municipality, parentId: mapping.kamRegionId });
      treeList.push({
        ...municipality,
        parentId: mapping.customerServiceRegionId,
      });
      const municipalityCopy = cloneDeep(municipality);
      const poMunicipality = {
        ...municipalityCopy,
        parentId: mapping.poHoldingRegionId,
      };

      mapping.adminRegionId.forEach((adminRegionType: string) => {
        const adminRegionMunicipality = {
          ...municipality,
          parentId: adminRegionType,
        };

        this.isGemisDSO ? (adminRegionMunicipality.selectable = false) : (adminRegionMunicipality.selectable = true);
        treeList.push(adminRegionMunicipality);
      });

      poMunicipality.selectable = false;
      poMunicipality.data.isDelete = true;
      treeList.push(poMunicipality);
    });

    return treeList;
  }

  private getPoHoldingRegionNode(poHoldingRegion: Region, regionType: any) {
    const treeNode = {} as Node;
    treeNode.item = {} as Item;
    treeNode.item.id = poHoldingRegion.guid;
    treeNode.item.name = poHoldingRegion.name;
    treeNode.item.data = {
      guid: poHoldingRegion.guid,
      name: poHoldingRegion.name,
      parent: null,
      isAdd: true,
      isEdit: false,
      isDelete: false,
      type: {
        guid: regionType.guid,
        name: regionType.name,
        code: regionType.code,
      },
    };
    treeNode.children = [];

    return treeNode;
  }

  makePoHoldingRegionNotEditable(poHoldingRegionNode: any) {
    poHoldingRegionNode[0].item.data.isEdit = false;
    return poHoldingRegionNode;
  }

  private mapToTreeNodeRegion(item: any, parent: any) {
    const treeNode = {} as Node;
    treeNode.item = {} as Item;
    treeNode.item.id = item.guid;
    treeNode.item.name = item.name;
    treeNode.item.data = {
      ...item.data,
      parent: isNil(parent) ? null : parent.data,
      isAdd: item.data.isAdd,
      isEdit: item.data.isEdit,
      isDelete: item.data.isDelete,
    };
    treeNode.item.selectable = item.selectable;
    treeNode.children = item.children;

    return treeNode;
  }

  treeViewDataBinded() {
    if (this.tree) {
      this.tree.toggleNodeExpansionByLevel(0);
      this.overLoaderService.ignoreChanges = false;
      this.overLoaderService.isLoading = false;
    }
  }

  selectDropdownParentRegion(option: any) {
    if (option.code === RegionTypeEnum.poHoldingRegion) {
      this.regionSelections = option.data;
      this.selectedParentRegion = option;
      this.isElementVisible = option.code === RegionTypeEnum.poHoldingRegion ? false : true;
      return;
    }
    const parentAdd = {} as Node;
    parentAdd.item = {
      id: 'AllRegion',
      name: 'Alle Regionen',
      filterMatch: true,
      data: {
        isEdit: false,
        isAdd: true,
        isDelete: false,
      },
      selectable: false,
    } as Item;
    parentAdd.children = option.data;
    this.isElementVisible = option.code === RegionTypeEnum.adminAreaRegion && this.isGemisDSO ? false : true;
    this.regionSelections = [parentAdd];
    this.selectedParentRegion = option;
  }

  canDeleteRegion(data: any) {
    let canDelete = true;
    let parentRegionData: any;
    let nodeRegionData: any;
    const regionType = data.data.type.code;
    const parentRegionType = data.data.parent;

    if (parentRegionType === null) {
      switch (regionType) {
        case RegionTypeEnum.backOfficeRegion:
          nodeRegionData = this.backOfficeRegions.find((y: any) => y.item.id === data.id);
          canDelete = nodeRegionData.children.length <= 0;
          break;
        case RegionTypeEnum.adminAreaRegion:
          nodeRegionData = this.adminRegions.find((y: any) => y.item.id === data.id);
          canDelete = nodeRegionData.children.length <= 0;
          break;
        case RegionTypeEnum.serviceRegion:
          nodeRegionData = this.serviceRegions.find((y: any) => y.item.id === data.id);
          canDelete = nodeRegionData.children.length <= 0;
          break;
      }
    } else {
      switch (parentRegionType.type.code) {
        case RegionTypeEnum.backOfficeRegion:
          parentRegionData = this.backOfficeRegions.find((x: any) => x.item.id === data.data.parent.guid);
          nodeRegionData = this.backOfficeRegions.find((x: any) => x.item.id === data.data.parent.guid).children.find((y: any) => y.item.id === data.id);
          canDelete = parentRegionData.children.length > 1 && nodeRegionData.children.length === 0;
          break;
        case RegionTypeEnum.serviceRegion:
          parentRegionData = this.serviceRegions.find((x: any) => x.item.id === data.data.parent.guid);
          nodeRegionData = this.serviceRegions.find((x: any) => x.item.id === data.data.parent.guid).children.find((y: any) => y.item.id === data.id);
          canDelete = parentRegionData.children.length > 1 && nodeRegionData.children.length === 0;
          break;
      }
    }
    return canDelete;
  }

  get isGemisDSO(): boolean {
    return Object.values(GemisTenant).includes(environment.tenant as GemisTenant);
  }

  deleteNode(node: any) {
    if (get(node, 'data.type.code') === 'MUNICIPALITY') {
      this.deleteMunicipality(node);
    } else {
      this.deleteRegion(node);
    }
  }

  deleteMunicipality(municipality: any) {
    const tempSelectedRegion = this.selectedParentRegion;
    const matRef = this.matDialog.open(NotificationDialogComponent, {
      data: {
        title: 'Gemeinde löschen',
        descriptionText: 'Sind Sie sicher, dass Sie diese Gemeinde entfernen möchten?',
        confirmText: 'Ja',
        declineText: 'Nein',
      },
      panelClass: ['-md'],
    });
    matRef.afterClosed().subscribe((confirm?: boolean) => {
      if (confirm) {
        this.overLoaderService.isLoading = true;
        const regionId = municipality.data.regionMapping.poHoldingRegionId;
        const municipalityId = municipality.id;
        this.regionMunicipalitiesService
          .deleteRegionMunicipalityMapping(regionId, municipalityId)
          .pipe(
            switchMap(() => {
              this.regionManagementData.updatePoHoldingRegionMunicipality(municipality.id, {
                poHoldingRegionId: null,
                poHoldingRegionName: null,
              });
              return this.loadData(tempSelectedRegion);
            }),
            catchError((error: HttpErrorResponse) => this.handleError(error)),
            takeUntil(this.unsubscribe$),
          )
          .subscribe({
            next: () => {
              this.overLoaderService.isLoading = false;
            },
            error: () => (this.overLoaderService.isLoading = false),
          });
      }
    });
  }

  deleteRegion(data: any) {
    const tempSelectedRegion = this.selectedParentRegion;

    let isAbleToDelete = true;
    isAbleToDelete = this.canDeleteRegion(data);

    if (isAbleToDelete) {
      const matRef = this.matDialog.open(NotificationDialogComponent, {
        data: {
          title: 'Region Löschen',
          descriptionText: 'Sind Sie sicher, dass Sie diese Region löschen möchten? Das Löschen kann nicht rückgängig gemacht werden.',
          confirmText: 'Ja',
          declineText: 'Nein',
        },
        panelClass: ['-md'],
      });
      matRef.afterClosed().subscribe((confirm) => {
        if (confirm) {
          this.overLoaderService.isLoading = true;

          this.regionService
            .delete(data.id)
            .pipe(
              switchMap(() => this.regionManagementData.deleteRegion(data.id)),
              switchMap(() => {
                this.overLoaderService.isLoading = false;
                return this.matDialog
                  .open(NotificationDialogComponent, {
                    data: {
                      descriptionText: 'Das Löschen war erfolgreich',
                      descriptionFaClass: 'fa-check-circle fa-2x',
                      descriptionFaColor: cssVariables['--confirm-success-color'],
                      hideClose: true,
                    },
                    panelClass: ['-md'],
                  })
                  .afterClosed();
              }),
              switchMap(() => this.loadData(tempSelectedRegion)),
              catchError((error: HttpErrorResponse) => this.handleError(error)),
              takeUntil(this.unsubscribe$)
            )
            .subscribe();
        } else {
          return;
        }
      });
    } else {
      this.matDialog.open(NotificationDialogComponent, {
        data: {
          descriptionText:
            'Um eine Region zu löschen, darf dieser Region keine Unterebene zugewiesen sein und zudem muss es weiterhin mindestens eine Region in der Hierarchieebene geben.',
          descriptionFaClass: 'fa-exclamation-triangle fa-2x',
          descriptionFaColor: cssVariables['--ep-error-color'],
          hideClose: true,
          confirmText: 'Ok',
        },
        panelClass: ['-sm'],
      });
    }
  }

  addChildren(node: any) {
    if (get(node, 'data.type.code') === RegionTypeEnum.poHoldingRegion) {
      this.addMunicipality(node);
    } else {
      this.addRegion(node);
    }
  }

  addMunicipality(data: any) {
    const tempSelectedRegion = this.selectedParentRegion;
    this.overLoaderService.isLoading = true;
    this.municipalityService
      .getAll(true, true)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((municipalities) => {
        const poholdingsMunicipalities = this.selectedParentRegion['data'][0].children;
        const filteredMunicipalities = differenceWith(municipalities, poholdingsMunicipalities, (a: any, b: any) => a.guid === b.item.id);
        const sorted = orderBy(filteredMunicipalities, ['municipalityName'], ['asc']);
        const treeData = sorted.map((a) => {
          const node = {} as Node;
          node.children = [];
          node.item = { selectable: true } as Item;
          node.item.id = a.guid;
          node.item.name = `${a.municipalityName} (${a.municipalityKey})`;
          node.item.data = a;
          return node;
        });
        this.matDialog
          .open(ListTreeDialogComponent, {
            data: {
              title: 'Gemeinde hinzufügen',
              treeData: treeData,
            },
            panelClass: ['-md'],
          })
          .afterClosed()
          .subscribe((selections) => {
            if (selections || selections.length > 0) {
              this.overLoaderService.isLoading = true;
              const $: Observable<any>[] = selections.map((municipality: any) => {
                return this.regionMunicipalitiesService.postMunicipalityRegionMapping({
                  regionId: data.id,
                  municipalityId: municipality.item.id,
                  modifyUser: this.authService.currentUser.guid,
                });
              });

              forkJoin($)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe({
                  next: (res: any[]) => {
                    res.forEach((d: any) => {
                      const municipality = selections.find((a: any) => a.item.id === d.municipalityId).item.data;
                      this.regionManagementData.updatePoHoldingRegionMunicipality(
                        municipality.guid,
                        {
                          poHoldingRegionId: data.id,
                          poHoldingRegionName: data.name,
                        },
                        municipality.municipalityName,
                        municipality.municipalityKey
                      );
                    });
                    this.loadData(tempSelectedRegion).pipe(takeUntil(this.unsubscribe$)).subscribe();
                    this.overLoaderService.isLoading = false;
                  },
                  error: () => {
                    this.loadData(tempSelectedRegion).pipe(takeUntil(this.unsubscribe$)).subscribe();
                    this.overLoaderService.isLoading = false;
                  },
                });
            }
          });
        this.overLoaderService.isLoading = false;
      });
  }

  reAssignKAM(data: any) {
    const tempSelectedRegion = this.selectedParentRegion;
    const matRef = this.matDialog.open<ReassignKamDialogComponent, Record<string, string>, NullableBoolean | undefined>(ReassignKamDialogComponent, {
      data: {
        municipalityGuid: data.data.guid,
        municipalityName: data.data.name,
        municipalityKey: data.data.municipalityKey,
        currKamName: `${data.data.primaryKam.firstName || ''} ${data.data.primaryKam.lastName || ''}`,
        currKamGuid: data.data.primaryKam.guid,
      },
      panelClass: ['-md'],
    });

    matRef
      .afterClosed()
      .pipe(
        switchMap((success?: NullableBoolean) => {
          if (!!success) {
            return this.showReassignSuccess()
              .afterClosed()
              .pipe(switchMap(() => this.loadData(tempSelectedRegion)));
          }

          if (success === false) {
            this.showReassignFailed();
            return of(null);
          }

          return of(null);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  showReassignSuccess = () =>
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: 'KAM-Zuordnung erfolgreich',
        descriptionFaClass: 'fa-check-circle fa-2x',
        descriptionFaColor: cssVariables['--confirm-success-color'],
        hideClose: true,
      },
      panelClass: ['-md', 'sensor-onboarding-confirmation'],
    });

  showReassignFailed = () =>
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: 'Zuordnung des verantwortlichen KAMs fehlgeschlagen. Bitte versuchen Sie es später noch einmal',
        descriptionFaClass: 'fa-exclamation-triangle fa-2x',
        descriptionFaColor: cssVariables['--ep-error-color'],
        hideClose: true,
      },
      panelClass: ['-md', 'sensor-onboarding-confirmation'],
    });

  trackByFn(_: number, item: any) {
    return item;
  }

  getChildrenRegionTypeId(parentRegionTypeId: string): string | undefined {
    const parentRegionTye = this.allRegionTypes.find((regionType: RegionType) => regionType.guid === parentRegionTypeId);
    switch (parentRegionTye?.code) {
      case RegionTypeEnum.backOfficeRegion:
        return this.allRegionTypes.find((regionType: RegionType) => regionType.code === RegionTypeEnum.kamRegion)?.guid;
      case RegionTypeEnum.serviceRegion:
        return this.allRegionTypes.find((regionType: RegionType) => regionType.code === RegionTypeEnum.customerServiceRegion)?.guid;
      default:
        return undefined;
    }
  }

  addRegion(regionItem: any) {
    const tempSelectedRegion = this.selectedParentRegion;
    let regionLevelText = this.selectedParentRegion.name;
    let regionTypeId: string | undefined = this.selectedParentRegion.guid;
    let parentRegionId = null;

    if (regionItem.id !== 'AllRegion') {
      regionLevelText = regionItem.name;
      regionTypeId = this.getChildrenRegionTypeId(regionItem.data.type.guid);
      parentRegionId = regionItem.id;
    }

    const addDialogRef = this.matDialog.open(AddEditRegionDialogComponent, {
      data: {
        title: 'Neue Region hinzufügen',
        label: 'Region Level: ',
        description: regionLevelText,
        secondLabel: 'Neue Region:',
        cancelText: 'Abbruch',
        saveText: 'Speichern',
        regionType: regionTypeId,
        parentRegionId: parentRegionId,
        isEdit: false,
        regionHierarchyTypeIds: this.regionHierarchyTypeIds,
      },
      panelClass: ['-md', 'edit-region-name-dialog'],
    });

    addDialogRef.afterClosed().subscribe((refresh) => {
      if (refresh) {
        this.matDialog.open(NotificationDialogComponent, {
          data: {
            descriptionText: 'Neue Region erfolgreich hinzugefügt',
            descriptionFaClass: 'fa-check-circle fa-2x',
            descriptionFaColor: cssVariables['--confirm-success-color'],
            hideClose: true,
          },
          panelClass: ['-md'],
        });
        this.loadData(tempSelectedRegion).pipe(takeUntil(this.unsubscribe$)).subscribe();
      }
    });
  }

  editNode(regionItem: any) {
    const tempSelectedRegion = this.selectedParentRegion;
    const regionTypeId = regionItem.data.type.guid;

    const editDialogRef = this.matDialog.open(AddEditRegionDialogComponent, {
      data: {
        title: 'Namen der Region ändern',
        label: 'Aktuell:',
        description: regionItem.name,
        secondLabel: 'Neu:',
        cancelText: 'Abbruch',
        saveText: 'Speichern',
        regionType: regionTypeId,
        editItem: regionItem,
        isEdit: true,
        regionHierarchyTypeIds: this.regionHierarchyTypeIds,
      },
      panelClass: ['-md', 'edit-region-name-dialog'],
    });

    editDialogRef
      .afterClosed()
      .pipe(
        switchMap((refresh) => {
          if (!refresh) {
            return of(null);
          }

          this.matDialog.open(NotificationDialogComponent, {
            data: {
              descriptionText: 'Das Umbenennen war erfolgreich',
              descriptionFaClass: 'fa-check-circle fa-2x',
              descriptionFaColor: cssVariables['--confirm-success-color'],
              hideClose: true,
            },
            panelClass: ['-md'],
          });
          return this.loadData(tempSelectedRegion);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  move() {
    const tempSelectedRegion = this.selectedParentRegion;
    const levelSelected = maxBy(
      this.tree?.selectedNodes.filter((a) => a.item.disabled === false),
      (a) => a.level
    );
    const selections = this.tree?.selectedNodes.filter((a) => a.level === levelSelected?.level);

    if (selections) {
      this.matDialog
        .open(ReassignmentDialogComponent, {
          panelClass: ['-md'],
          data: {
            selections: selections,
            hierarchy: this.selectedParentRegion,
          },
        })
        .afterClosed()
        .subscribe((refresh) => {
          if (refresh) {
            this.matDialog
              .open(NotificationDialogComponent, {
                data: {
                  descriptionText: 'Regionenzuordnung erfolgreich',
                  descriptionFaClass: 'fa-check-circle fa-2x',
                  descriptionFaColor: cssVariables['--confirm-success-color'],
                  hideClose: true,
                  closeButton: true,
                },
                panelClass: ['-md'],
              })
              .afterClosed()
              .subscribe(() => {
                this.loadData(tempSelectedRegion).pipe(takeUntil(this.unsubscribe$)).subscribe();
              });
          }

          if (refresh === null) {
            this.matDialog.open(NotificationDialogComponent, {
              data: {
                descriptionText: 'Regionenzuordnung nicht erfolgreich. Bitte versuchen Sie es später nochmal.',
                descriptionFaClass: 'fa-exclamation-triangle fa-2x',
                descriptionFaColor: cssVariables['--ep-error-color'],
              },
              panelClass: ['-md'],
            });
          }
        });
    }
  }

  treeSelect(data: any) {
    if (data.isChecked) {
      const levelToDisable = data.node.level === 3 ? 2 : 3;
      this.tree?.disableNodeByLevel(levelToDisable, true);
    }

    if (!data.isChecked && this.tree?.selectedNodes.length === 0) {
      this.tree.disableNodeByLevel(2, false);
      this.tree.disableNodeByLevel(3, false);
    }
  }

  get hasSelections() {
    if (this.tree) {
      const levelSelected = maxBy(
        this.tree.selectedNodes.filter((a) => a.item.disabled === false),
        (a) => a.level
      );
      const selections = this.tree.selectedNodes.filter((a) => a.level === levelSelected?.level);
      return selections.length > 0;
    }

    return false;
  }

  get regionHierarchyTypeIds() {
    if (this.selectedParentRegion.code === RegionTypeEnum.backOfficeRegion) {
      return this.allRegionTypes.filter((a) => a.code === RegionTypeEnum.backOfficeRegion || a.code === RegionTypeEnum.kamRegion).map((a) => a.guid);
    }

    if (this.selectedParentRegion.code === RegionTypeEnum.serviceRegion) {
      return this.allRegionTypes.filter((a) => a.code === RegionTypeEnum.serviceRegion || a.code === RegionTypeEnum.customerServiceRegion).map((a) => a.guid);
    }

    if (this.selectedParentRegion.code === RegionTypeEnum.adminAreaRegion) {
      return this.allRegionTypes.filter((a) => a.code === RegionTypeEnum.adminAreaRegion).map((a) => a.guid);
    }

    return [];
  }

  getAddIconMarginAdjustment(array: any) {
    const maxLength = 25;
    let longestNameLength = 0;

    for (const element of array.data) {
      if (element.item.name.length > longestNameLength) {
        longestNameLength = element.item.name.length;
      }
    }
    this.marginAdjustment = longestNameLength > maxLength ? 5 * (longestNameLength - maxLength) : 0;
  }

  get poHoldingRegionCode() {
    return RegionTypeEnum.poHoldingRegion;
  }

  private checkifRegionOrphan(regionMunicipality?: MunicipalityRegionHierarchy): boolean {
    return isNil(regionMunicipality?.kamRegionId) && isNil(regionMunicipality?.customerServiceRegionId);
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const errorMessageModal = this.translateService.instant('module.shared.errorMessage.generalErrorText');
    this.matDialog.open<GeneralErrorDialogComponent, IGeneralErrorDialogComponent, unknown>(GeneralErrorDialogComponent, {
      data: {
        descriptionText: errorMessageModal,
      },
      panelClass: ['-md'],
    });
    this.overLoaderService.isLoading = false;
    return throwError(() => error);
  }
}
